import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  message,
  Popconfirm,
  Typography,
  Statistic,
  Row,
  Col,
  Drawer,
  Divider,
  Select,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  SwapOutlined,
  HistoryOutlined,
  GlobalOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;

interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  rounding: number;
  decimalPlaces: number;
  active: boolean;
  latestRate?: number;
  rates?: CurrencyRate[];
}

interface CurrencyRate {
  id: string;
  currencyId: string;
  rate: number;
  name: string; // date stored as name
}

interface CurrencySummary {
  baseCurrency: string;
  currencies: {
    code: string;
    name: string;
    symbol: string;
    latestRate: number | null;
    rateDate: string | null;
  }[];
}

interface ConversionResult {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  rate: number;
  date: string;
}

const CurrencyManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [summary, setSummary] = useState<CurrencySummary | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRateModalVisible, setIsRateModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [rateHistory, setRateHistory] = useState<{ date: string; rate: number }[]>([]);
  const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [form] = Form.useForm();
  const [rateForm] = Form.useForm();
  const [convertForm] = Form.useForm();

  useEffect(() => {
    fetchCurrencies();
    fetchSummary();
  }, []);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/accounting/currencies?includeInactive=true');
      setCurrencies(response.data || []);
    } catch (error: any) {
      message.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await apiService.get('/accounting/currencies/summary');
      setSummary(response.data);
    } catch (error: any) {
      console.error('Failed to load currency summary:', error);
    }
  };

  // Rates are stored in the DB as "1 HKD = X foreign currency" (e.g. USD: 0.1280)
  // but the UI now presents the more intuitive "1 foreign = X HKD" direction
  // (e.g. USD: 7.8125). These helpers convert between the two.
  const displayRate = (storedRate: number | null | undefined): string => {
    if (storedRate === null || storedRate === undefined || storedRate === 0) return '-';
    return (1 / Number(storedRate)).toFixed(4);
  };

  const toStoredRate = (displayed: number): number => {
    if (!displayed || displayed <= 0) return 0;
    return 1 / displayed;
  };

  const formatRate = (rate: number | null) => displayRate(rate);

  const handleCreate = () => {
    setEditingCurrency(null);
    form.resetFields();
    form.setFieldsValue({ decimalPlaces: 2, rounding: 0.01 });
    setIsModalVisible(true);
  };

  const handleEdit = (record: Currency) => {
    setEditingCurrency(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      symbol: record.symbol,
      decimalPlaces: record.decimalPlaces,
      rounding: record.rounding
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingCurrency) {
        await apiService.put(`/accounting/currencies/${editingCurrency.id}`, values);
        message.success('Currency updated');
      } else {
        await apiService.post('/accounting/currencies', values);
        message.success('Currency created');
      }

      setIsModalVisible(false);
      fetchCurrencies();
      fetchSummary();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleToggle = async (currency: Currency) => {
    try {
      await apiService.post(`/accounting/currencies/${currency.id}/toggle`, {
        active: !currency.active
      });
      message.success(`Currency ${currency.active ? 'deactivated' : 'activated'}`);
      fetchCurrencies();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle currency');
    }
  };

  const handleInitialize = async () => {
    try {
      const response = await apiService.post('/accounting/currencies/initialize');
      const count = response.data?.length || 0;
      message.success(`Initialized ${count} common currencies`);
      fetchCurrencies();
      fetchSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to initialize currencies');
    }
  };

  const handleAddRate = (currency: Currency) => {
    setSelectedCurrency(currency);
    rateForm.resetFields();
    rateForm.setFieldsValue({ date: dayjs() });
    setIsRateModalVisible(true);
  };

  const handleRateSubmit = async () => {
    try {
      const values = await rateForm.validateFields();
      // The form takes the user-natural direction "1 {currency} = X HKD" (e.g. 7.85).
      // The backend still expects the legacy "per 1 HKD" storage convention,
      // so invert before sending.
      const storedRate = toStoredRate(Number(values.rate));
      await apiService.post('/accounting/exchange-rates', {
        currencyId: selectedCurrency?.id,
        rate: storedRate,
        date: values.date.format('YYYY-MM-DD')
      });
      message.success('Exchange rate added');
      setIsRateModalVisible(false);
      fetchCurrencies();
      fetchSummary();
    } catch (error: any) {
      message.error(error.message || 'Failed to add rate');
    }
  };

  const handleViewHistory = async (currency: Currency) => {
    setSelectedCurrency(currency);
    try {
      const fromDate = dayjs().subtract(90, 'days').format('YYYY-MM-DD');
      const toDate = dayjs().format('YYYY-MM-DD');
      const response = await apiService.get(
        `/accounting/exchange-rates/${currency.id}/history?fromDate=${fromDate}&toDate=${toDate}`
      );
      setRateHistory(response.data || []);
      setHistoryDrawerVisible(true);
    } catch (error: any) {
      message.error('Failed to load rate history');
    }
  };

  const handleConvert = () => {
    convertForm.resetFields();
    convertForm.setFieldsValue({ fromCurrency: 'HKD' });
    setConversionResult(null);
    setIsConvertModalVisible(true);
  };

  const handleConvertSubmit = async () => {
    try {
      const values = await convertForm.validateFields();
      const response = await apiService.post('/accounting/currencies/convert', {
        fromCurrencyCode: values.fromCurrency,
        toCurrencyCode: values.toCurrency,
        amount: values.amount,
        date: values.date?.format('YYYY-MM-DD')
      });
      setConversionResult(response.data);
    } catch (error: any) {
      message.error(error.message || 'Conversion failed');
    }
  };

  const columns: ColumnsType<Currency> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 80,
      render: (code: string) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 80,
      align: 'center'
    },
    {
      title: 'Decimals',
      dataIndex: 'decimalPlaces',
      key: 'decimalPlaces',
      width: 80,
      align: 'center'
    },
    {
      title: 'Latest Rate (→ HKD)',
      key: 'latestRate',
      width: 150,
      align: 'right',
      render: (_, record) => {
        const summaryItem = summary?.currencies.find(c => c.code === record.code);
        return <Text>{formatRate(summaryItem?.latestRate || null)}</Text>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="Add Rate">
            <Button type="text" icon={<PlusOutlined />} onClick={() => handleAddRate(record)} />
          </Tooltip>
          <Tooltip title="Rate History">
            <Button type="text" icon={<HistoryOutlined />} onClick={() => handleViewHistory(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={record.active ? 'Deactivate' : 'Activate'}>
            <Popconfirm
              title={`${record.active ? 'Deactivate' : 'Activate'} this currency?`}
              onConfirm={() => handleToggle(record)}
            >
              <Button
                type="text"
                icon={record.active ? <StopOutlined /> : <CheckCircleOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  const historyColumns: ColumnsType<{ date: string; rate: number }> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Rate (1 unit → HKD)',
      dataIndex: 'rate',
      key: 'rate',
      align: 'right',
      render: (rate: number) => displayRate(rate)
    }
  ];

  const activeCurrencies = currencies.filter(c => c.active);

  return (
    <>
      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Base Currency"
              value={summary?.baseCurrency || 'HKD'}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Currencies"
              value={activeCurrencies.length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="With Rates"
              value={summary?.currencies.filter(c => c.latestRate !== null).length || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Currencies"
              value={currencies.length}
            />
          </Card>
        </Col>
      </Row>

      {/* Currency Table */}
      <Card
        title={
          <Space>
            <GlobalOutlined />
            <span>Currency Management</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<SwapOutlined />} onClick={handleConvert}>
              Convert
            </Button>
            <Popconfirm
              title="Initialize common currencies (HKD, USD, EUR, etc.)?"
              onConfirm={handleInitialize}
            >
              <Button icon={<ReloadOutlined />}>Initialize Common</Button>
            </Popconfirm>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Currency
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={currencies}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* Create/Edit Currency Modal */}
      <Modal
        title={editingCurrency ? 'Edit Currency' : 'New Currency'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Currency Code"
                rules={[
                  { required: true, message: 'Please enter code' },
                  { len: 3, message: 'Code must be 3 characters' }
                ]}
              >
                <Input
                  placeholder="e.g., USD"
                  maxLength={3}
                  style={{ textTransform: 'uppercase' }}
                  disabled={!!editingCurrency}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="symbol"
                label="Symbol"
                rules={[{ required: true, message: 'Please enter symbol' }]}
              >
                <Input placeholder="e.g., $" maxLength={5} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="name"
            label="Currency Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., US Dollar" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="decimalPlaces"
                label="Decimal Places"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} max={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rounding"
                label="Rounding"
                rules={[{ required: true }]}
              >
                <InputNumber min={0.0001} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Add Exchange Rate Modal */}
      <Modal
        title={`Add Rate for ${selectedCurrency?.code}`}
        open={isRateModalVisible}
        onOk={handleRateSubmit}
        onCancel={() => setIsRateModalVisible(false)}
        width={400}
      >
        <Form form={rateForm} layout="vertical">
          <Form.Item
            name="rate"
            label={`Exchange Rate (1 ${selectedCurrency?.code || 'unit'} = X HKD)`}
            tooltip="Enter how many HKD equal one unit of this currency. Stored internally as the inverse (per 1 HKD)."
            rules={[
              { required: true, message: 'Please enter rate' },
              { type: 'number', min: 0.0001, message: 'Rate must be positive' },
            ]}
          >
            <InputNumber
              min={0.0001}
              step={0.0001}
              precision={4}
              style={{ width: '100%' }}
              placeholder={`e.g., 7.8125 for 1 ${selectedCurrency?.code || 'USD'} = 7.8125 HKD`}
            />
          </Form.Item>
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Currency Conversion Modal */}
      <Modal
        title="Currency Conversion"
        open={isConvertModalVisible}
        onCancel={() => setIsConvertModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsConvertModalVisible(false)}>
            Close
          </Button>,
          <Button key="convert" type="primary" onClick={handleConvertSubmit}>
            Convert
          </Button>
        ]}
        width={500}
      >
        <Form form={convertForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fromCurrency"
                label="From Currency"
                rules={[{ required: true }]}
              >
                <Select>
                  {activeCurrencies.map(c => (
                    <Select.Option key={c.code} value={c.code}>
                      {c.symbol} {c.code}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="toCurrency"
                label="To Currency"
                rules={[{ required: true }]}
              >
                <Select>
                  {activeCurrencies.map(c => (
                    <Select.Option key={c.code} value={c.code}>
                      {c.symbol} {c.code}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true }]}
          >
            <InputNumber
              min={0.01}
              step={1}
              style={{ width: '100%' }}
              placeholder="Enter amount"
            />
          </Form.Item>
          <Form.Item name="date" label="Date (optional)">
            <DatePicker style={{ width: '100%' }} placeholder="Leave empty for latest rate" />
          </Form.Item>
        </Form>

        {conversionResult && (
          <>
            <Divider />
            <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="From"
                    value={conversionResult.fromAmount}
                    suffix={conversionResult.fromCurrency}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="To"
                    value={conversionResult.toAmount}
                    suffix={conversionResult.toCurrency}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                Rate: {conversionResult.rate.toFixed(6)}
              </Text>
            </Card>
          </>
        )}
      </Modal>

      {/* Rate History Drawer */}
      <Drawer
        title={`Exchange Rate History: ${selectedCurrency?.code}`}
        placement="right"
        width={500}
        open={historyDrawerVisible}
        onClose={() => setHistoryDrawerVisible(false)}
      >
        <Table
          columns={historyColumns}
          dataSource={rateHistory}
          rowKey="date"
          pagination={false}
          size="small"
        />
        {rateHistory.length === 0 && (
          <Text type="secondary">No rate history found for the last 90 days</Text>
        )}
      </Drawer>
    </>
  );
};

export default CurrencyManagement;
